import { useCallback, useEffect, useRef } from 'react';

interface UseKeyboardScrollOptions {
  offset?: number;
  smooth?: boolean;
}

export const useKeyboardScroll = (options: UseKeyboardScrollOptions = {}) => {
  const { offset = 100, smooth = true } = options;
  const lastScrollPosition = useRef<number>(0);
  const isKeyboardOpen = useRef<boolean>(false);

  const scrollToElement = useCallback((element: HTMLElement) => {
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const elementBottom = rect.bottom;
    const elementTop = rect.top;

    // Check if element is hidden by keyboard
    if (elementBottom > windowHeight - offset || elementTop < offset) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetScrollTop = scrollTop + elementTop - offset;

      window.scrollTo({
        top: targetScrollTop,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
  }, [offset, smooth]);

  const handleFocus = useCallback((event: FocusEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
      // Store current scroll position
      lastScrollPosition.current = window.pageYOffset || document.documentElement.scrollTop;

      // Small delay to ensure keyboard is fully opened
      setTimeout(() => {
        scrollToElement(target);
        isKeyboardOpen.current = true;
      }, 300);
    }
  }, [scrollToElement]);

  const handleBlur = useCallback(() => {
    isKeyboardOpen.current = false;
  }, []);

  const handleResize = useCallback(() => {
    // If keyboard was open and window height increased, keyboard likely closed
    if (isKeyboardOpen.current) {
      const currentHeight = window.innerHeight;
      const previousHeight = window.visualViewport?.height || currentHeight;

      if (currentHeight > previousHeight) {
        isKeyboardOpen.current = false;
        // Restore scroll position if needed
        if (lastScrollPosition.current > 0) {
          window.scrollTo({
            top: lastScrollPosition.current,
            behavior: smooth ? 'smooth' : 'auto'
          });
        }
      }
    }
  }, [smooth]);

  useEffect(() => {
    // Add event listeners
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    window.addEventListener('resize', handleResize);

    // Handle visual viewport changes (better for mobile keyboards)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      window.removeEventListener('resize', handleResize);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, [handleFocus, handleBlur, handleResize]);

  // Return a function to manually scroll to an element
  return { scrollToElement };
};
